import type {
  MarketableCodes as CanonicalMarketableCodes,
  NotMarketableCodes as CanonicalNotMarketableCodes,
  GlobalSubstanceListEntry,
} from "@color-finder/color-finder-api/models";
import CancelSharpIcon from "@mui/icons-material/CancelSharp";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import HelpSharpIcon from "@mui/icons-material/HelpSharp";
import {
  Alert,
  AlertTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useAutocompleteOptions } from "hooks/autocomplete-data";
import type { FC } from "react";
import { CountryFlagDataDisplay } from "../dataDisplay/CountryFlagDisplay";

// Canonical definitions live in @color-finder/color-finder-api/models
// (packages/lib/color-finder-api/src/models/colored-material.ts). They are
// re-declared here because CRA's Rollup-based Storybook build cannot
// statically trace the named exports through the CJS __exportStar boundary
// of the shared models package (same issue as service/tracking.ts). Keep
// both lists in sync when adding values — the AssertEqual guard below fails
// to type-check if they drift.
const MARKETABLE_CODES_TUPLE = ["01", "61"] as const;
const NOT_MARKETABLE_CODES_TUPLE = ["02"] as const;
const MarketableCodes: readonly string[] = MARKETABLE_CODES_TUPLE;
const NotMarketableCodes: readonly string[] = NOT_MARKETABLE_CODES_TUPLE;

type AssertEqual<A, B> = [A] extends [B]
  ? [B] extends [A]
    ? true
    : never
  : never;
type _MarketableCodesInSync = AssertEqual<
  (typeof MARKETABLE_CODES_TUPLE)[number],
  (typeof CanonicalMarketableCodes)[number]
>;
type _NotMarketableCodesInSync = AssertEqual<
  (typeof NOT_MARKETABLE_CODES_TUPLE)[number],
  (typeof CanonicalNotMarketableCodes)[number]
>;
const _syncChecks: [_MarketableCodesInSync, _NotMarketableCodesInSync] = [
  true,
  true,
];
void _syncChecks;

export interface GlobalSubstanceListOverviewProps {
  value: Array<GlobalSubstanceListEntry>;
}

export const GlobalSubstanceListOverview: FC<
  GlobalSubstanceListOverviewProps
> = ({ value }) => {
  const [options, error, isLoading] = useAutocompleteOptions(
    "substanceList",
    undefined,
    undefined,
  );

  const merged = options?.map(
    (o) =>
      value.find((v) => o.value === v.regulatoryList) ?? {
        regulatoryList: o.value,
        countryIso: o.countryIso,
        marketabilityLabel: "no data",
        marketabilityCode: "00",
      },
  );

  if (isLoading) {
    return (
      <>
        <Skeleton height={28} />
        <Skeleton height={28} />
      </>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ fontSize: "0.9em", px: 1, py: 0.5 }}>
        <AlertTitle sx={{ fontSize: "1.1em" }}>
          An unexpected error occurred
        </AlertTitle>
        {error.message}
      </Alert>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {merged?.map((v) => (
              <TableCell
                key={v.regulatoryList}
                sx={{ paddingBottom: 0.25 }}
                align="center"
                padding="none"
              >
                <Tooltip title={v.regulatoryList}>
                  <span>
                    <CountryFlagDataDisplay
                      isoCode={v.countryIso!}
                      size="small"
                    />
                  </span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {merged?.map((v) => (
              <TableCell
                key={v.regulatoryList}
                sx={{ borderBottom: "none", paddingTop: 0.5 }}
                align="center"
                padding="none"
              >
                <Tooltip
                  title={`${v.marketabilityCode}: ${v.marketabilityLabel}`}
                >
                  <span>
                    {MarketableCodes.includes(v.marketabilityCode) && (
                      <CheckCircleSharpIcon
                        color={"success"}
                        fontSize={"small"}
                      />
                    )}
                    {NotMarketableCodes.includes(v.marketabilityCode) && (
                      <CancelSharpIcon color={"error"} fontSize={"small"} />
                    )}
                    {![...MarketableCodes, ...NotMarketableCodes].includes(
                      v.marketabilityCode,
                    ) && <HelpSharpIcon color={"warning"} fontSize={"small"} />}
                  </span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
